import { buildBridgeInlineJS } from "./unlayerBridgeScript";
import { applyImageUrlById, collectDesignImages, saveDesign } from "./unlayerDesign";

export const UNLAYER_UPLOAD_BRIDGE_SOURCE = "cohora-unlayer-upload";
export const UNLAYER_EDITOR_ORIGIN = "https://editor.unlayer.com";

export const BRIDGE_REPORT_INTERVAL_MS = 400;
export const BRIDGE_SET_URL_TIMEOUT_MS = 800;
export const PICK_FILE_FOCUS_FALLBACK_MS = 300;

/** @type {{ editor: any, description: string, onUploadError?: Function, onSelectionChange?: Function } | null} */
let activeUploadContext = null;
let windowListenerAttached = false;

const findEditorIframe = (editor) =>
  editor?.frame?.iframe ||
  document.querySelector("iframe[src*='editor.unlayer.com']") ||
  document.querySelector('[id^="editor-"] iframe') ||
  null;

export const getEditorContentWindow = (editor) => findEditorIframe(editor)?.contentWindow || null;

const getEditorTargetOrigin = (editor) => {
  const iframe = findEditorIframe(editor);
  const src = iframe?.src;
  if (src) {
    try {
      return new URL(src).origin;
    } catch {
      // fall through
    }
  }
  return UNLAYER_EDITOR_ORIGIN;
};

const isTrustedBridgeOrigin = (origin) => {
  if (origin === UNLAYER_EDITOR_ORIGIN) {
    return true;
  }
  if (typeof window !== "undefined" && origin === window.location.origin) {
    return true;
  }
  // Allow the live editor iframe origin (path/host variants under unlayer.com)
  try {
    const hostname = new URL(origin).hostname;
    return hostname === "unlayer.com" || hostname.endsWith(".unlayer.com");
  } catch {
    return false;
  }
};

export const buildUnlayerImageBridgeInlineJS = () =>
  buildBridgeInlineJS({
    source: UNLAYER_UPLOAD_BRIDGE_SOURCE,
    appOrigin: typeof window !== "undefined" ? window.location.origin : "*",
    reportIntervalMs: BRIDGE_REPORT_INTERVAL_MS,
    // shouldRender + customCSS hide Unlayer upload UI; keep text-matching off by default
    hideNativeUpload: false,
  });

const onWindowMessage = (event) => {
  if (!isTrustedBridgeOrigin(event.origin)) {
    return;
  }
  if (event.data?.source !== UNLAYER_UPLOAD_BRIDGE_SOURCE) {
    return;
  }

  if (event.data.type === "SELECTION") {
    activeUploadContext?.onSelectionChange?.({
      isImage: !!event.data.isImage,
      currentUrl: event.data.currentUrl || "",
    });
    return;
  }

  if (event.data.type === "PONG") {
    activeUploadContext?.onSelectionChange?.({ bridgeAlive: true });
  }
};

export const attachBridgeWindowListener = () => {
  if (typeof window === "undefined" || windowListenerAttached) {
    return;
  }
  window.addEventListener("message", onWindowMessage);
  windowListenerAttached = true;
};

export const detachBridgeWindowListener = () => {
  if (typeof window === "undefined" || !windowListenerAttached) {
    return;
  }
  window.removeEventListener("message", onWindowMessage);
  windowListenerAttached = false;
};

export const setActiveUploadContext = (context) => {
  activeUploadContext = context;
};

export const clearActiveUploadContext = () => {
  activeUploadContext = null;
};

export const teardownUnlayerBridge = (editor) => {
  const target = getEditorContentWindow(editor);
  if (target) {
    try {
      target.postMessage(
        { source: UNLAYER_UPLOAD_BRIDGE_SOURCE, type: "TEARDOWN" },
        getEditorTargetOrigin(editor),
      );
    } catch {
      // iframe may already be gone
    }
  }
  clearActiveUploadContext();
  detachBridgeWindowListener();
};

export const setSelectedImageUrlViaBridge = (editor, url, timeoutMs = BRIDGE_SET_URL_TIMEOUT_MS) =>
  new Promise((resolve, reject) => {
    const target = getEditorContentWindow(editor);
    if (!target) {
      reject(new Error("Unlayer iframe is not available"));
      return;
    }

    const timer = window.setTimeout(() => {
      window.removeEventListener("message", onMessage);
      reject(new Error("Bridge timeout"));
    }, timeoutMs);

    const onMessage = (event) => {
      if (!isTrustedBridgeOrigin(event.origin)) {
        return;
      }
      if (event.data?.source !== UNLAYER_UPLOAD_BRIDGE_SOURCE) {
        return;
      }
      if (event.data.type === "SET_IMAGE_URL_OK") {
        window.clearTimeout(timer);
        window.removeEventListener("message", onMessage);
        resolve(url);
        return;
      }
      if (event.data.type === "SET_IMAGE_URL_FAILED") {
        window.clearTimeout(timer);
        window.removeEventListener("message", onMessage);
        reject(new Error(event.data.reason || "Failed to set image URL"));
      }
    };

    window.addEventListener("message", onMessage);
    target.postMessage(
      { source: UNLAYER_UPLOAD_BRIDGE_SOURCE, type: "SET_IMAGE_URL", url },
      getEditorTargetOrigin(editor),
    );
  });

/**
 * Prefer bridge (preserves undo), verify via saveDesign, fall back to loadDesign patch.
 */
export const applyImageToTarget = async (editor, { url, size, imageId, useBridge = false } = {}) => {
  if (!editor || !url) {
    throw new Error("Editor and url are required");
  }

  if (useBridge) {
    try {
      await setSelectedImageUrlViaBridge(editor, url);
      const design = await saveDesign(editor);
      if (collectDesignImages(design).some((img) => img.url === url)) {
        return url;
      }
      console.warn("Bridge reported OK but design still missing url; falling back to loadDesign");
    } catch (error) {
      console.warn("Bridge set image URL failed, falling back to loadDesign", error);
    }
  }

  if (!imageId) {
    throw new Error("imageId is required when bridge cannot apply the image");
  }

  return applyImageUrlById(editor, imageId, url, size);
};
